import React from 'react';
import { motion } from 'framer-motion';
import { Quote, Linkedin, ExternalLink, Briefcase } from 'lucide-react';

// Helper to build the header string with pipes, avoiding redundancy
function buildHeader({ role, company }) {
  // If role contains company, don't repeat
  if (role && company && role.toLowerCase().includes(company.toLowerCase())) {
    return role;
  }
  // If both present, join with pipe
  if (role && company) {
    return `${role} | ${company}`;
  }
  // If only one present
  return role || company || '';
}

const TestimonialsUnlocked = () => {
  const testimonials = [
    {
      name: 'Martin Kristensson',
      role: 'VP, Autonomous Driving',
      company: 'Volvo Cars',
      logo: 'volvo-cars.svg',
      linkedin: 'https://www.linkedin.com/in/mkristensson/',
      date: '',
      short: 'Neil consistently delivered exceptional results managing strategic partnerships at Volvo. His analytical skills, industry insight, and proactive approach made him instrumental in driving new business opportunities.',
      full: `I am happy to write this recommendation for Neil, who has been an outstanding member of my team during 2022 and 2023. Neil worked as a business developer and partner manager, reporting directly to me.\n\nNeil is a highly skilled and dedicated professional, known for his social nature, analytical mindset, and strong work ethic. Throughout his tenure at Volvo, Neil successfully managed a portfolio of key partners, consistently delivering exceptional results. I was particularly impressed by his ability to identify and cultivate new business opportunities aligned with our strategic goals. This required in-depth insight from the car industry, as well as an understanding of business models from other domains. Neil is dedicated to the work and was instrumental in establishing the partner management team. He has an extensive network as well as a keen understanding of the tech domain. He consistently went above and beyond to meet deadlines and exceed expectations.\n\nI wholeheartedly recommend Neil for any future opportunities. He is a quick thinker, analytic, hardworking – and generally a great guy to have around! I have no doubt that he will make valuable contributions to any organization fortunate enough to have him on board. Please don't hesitate to reach out to me if you would like further information or have any questions about Neil's qualifications.`,
      relatedProjects: ['Product & Autonomous Strategy']
    },
    {
      name: 'Sunny Wong',
      role: '',
      company: 'Accenture',
      logo: 'accenture.svg',
      linkedin: 'https://www.linkedin.com/in/sunnykswong/',
      date: '',
      short: 'Neil expertly navigated challenging stakeholder discussions, translating complex technical details into clear business terms. His strategic insight, clarity, and confidence were critical in managing scope and aligning stakeholders.',
      full: `Neil demonstrated exceptional preparation, strategic communication, and firm but professional management of challenging stakeholder discussions during a critical project meeting, clearly translating technical information into actionable business language.\n\n"Neil was outstanding in today's meeting. He handled tough questions confidently and translated complex technical discussions into clear business terms. He professionally addressed suggested scope changes by firmly referring to prior agreements, demonstrating strong strategic insight and stakeholder management skills. It was clear Neil knew the subject matter thoroughly and communicated it effectively—a great example of leadership and clarity."`,
      relatedProjects: ['AI Strategy Transformation']
    },
    {
      name: 'Dondi S. Schneider',
      role: 'Senior Managing Director',
      company: 'Accenture',
      logo: 'accenture.svg',
      linkedin: 'https://www.linkedin.com/in/dondischneider/',
      date: '',
      short: 'Neil is an exceptional business operator whose strategic perspective and operational effectiveness greatly elevate our team. His contributions consistently drive impactful results.',
      full: `Neil is recognized as a highly capable business operator whose contributions significantly enhance team effectiveness and strategic execution.\n\n"This is a fantastic reflection of our teamwork and recognition of Neil's excellence. Neil consistently demonstrates strong strategic insight and operational effectiveness, making him an outstanding asset to our program. Proud to have him as part of the team."`,
      relatedProjects: ['AI Strategy Transformation', 'Digital Transformation ROI Model']
    },
    {
      name: 'Pankaj Narang',
      role: 'Head of Agile',
      company: 'DTE Energy',
      logo: 'dte.svg',
      linkedin: 'https://www.linkedin.com/in/pankaj2015/',
      date: '',
      short: 'Neil led the transition of our Reports and Controls initiative seamlessly from assessment to execution, emphasizing collaborative knowledge-sharing. His proactive leadership turned complex deliverables into clear, manageable outcomes.',
      full: `Neil played an instrumental role in seamlessly transitioning a significant Reports and Controls initiative from assessment to execution, emphasizing collaborative knowledge sharing and smooth operational handover.\n\n"I want to acknowledge Neil for his exceptional support in transitioning our Reports and Controls initiative smoothly from the assessment to execution phase. Neil ensured this transition was not merely a hand-off, but a true collaborative 'hand-shake,' effectively transforming complex deliverables into clear, actionable tasks. His proactive approach in facilitating cooperation and knowledge sharing was pivotal to our success. Outstanding work!"`,
      relatedProjects: ['Reports & Controls Initiative']
    },
    {
      name: 'Jennifer McAdams',
      role: 'Manager-Strategic Market Planning and Development',
      company: 'DTE Energy',
      logo: 'dte.svg',
      linkedin: 'https://www.linkedin.com/in/jennifer-mcadams-67889813/',
      date: '',
      short: 'Neil delivered exceptional results with a 99.7% conversion rate, far exceeding expectations. His dedication and precision saved our team hundreds of hours in manual corrections. I look forward to seeing his continued impact on future projects.',
      full: `Neil,\n\nYou were truly a breath of fresh air when I was feeling uneasy at Go-Live. I really appreciated your consistency and patience to deliver a 99.7% conversion rate. Way higher than previous consultants who could only estimate 50% conversion. With your hard work, you saved hundreds of hours of manual effort to correct system records. Best of luck on the Duke project — they're going to love working with you.`,
      relatedProjects: ['DTE Energy Go-Live Project']
    }
  ];

  // Helper to render logo or fallback
  function renderLogo(logo, fallback) {
    try {
      // eslint-disable-next-line import/no-dynamic-require, global-require
      return <img src={require(`../assets/logos/${logo}`)} alt={fallback} className="h-10 w-10 object-contain rounded bg-white border border-primary-100" />;
    } catch {
      return <span className="text-primary-800 font-bold text-sm">{fallback}</span>;
    }
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="section bg-gradient-to-br from-primary-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h1 className="section-title">Testimonials</h1>
            <p className="section-subtitle">
              What executives and leaders say about working with me
            </p>
            <p className="text-primary-700 text-lg leading-relaxed">
              These testimonials reflect the strategic impact and measurable results I've delivered 
              across Fortune 500 companies, high-growth startups, and innovative organizations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="section bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((t, idx) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="card group hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg">
                      {renderLogo(t.logo, t.company || t.name)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary-900 flex items-center gap-2">
                        {t.name}
                      </h3>
                      {buildHeader(t) && <p className="text-sm text-primary-600">{buildHeader(t)}</p>}
                      {t.date && <p className="text-xs text-primary-400 mt-1">{t.date}</p>}
                    </div>
                  </div>
                  <Quote size={20} className="text-primary-300" />
                </div>
                <div className="mb-4 flex flex-row items-end justify-between">
                  <div className="flex items-end">
                    <Quote size={20} className="text-primary-300 rotate-180" />
                  </div>
                  <div className="flex-1 ml-2">
                    <p className="text-primary-700 leading-relaxed">
                      {t.short}
                    </p>
                    <a href={`#full-${t.name.replace(/\s/g, '').toLowerCase()}`} className="text-primary-600 underline text-xs hover:text-primary-900">Read full testimonial</a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="flex justify-center mt-8 mb-4"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-primary-400 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-primary-400 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>

      {/* Full Testimonials Section Header */}
      <section className="section bg-primary-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-4"
          >
            <h2 className="section-title">Full Testimonials</h2>
            <p className="section-subtitle">
              Detailed feedback and recommendations from executives and leaders
            </p>
          </motion.div>
        </div>
      </section>

      {/* Full Testimonials Section */}
      {testimonials.map((t, idx) => (
        <section className="section bg-white" id={`full-${t.name.replace(/\s/g, '').toLowerCase()}`} key={t.name}>
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: idx * 0.1 }}
              className="card"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 flex items-center justify-center rounded-lg">
                    {renderLogo(t.logo, t.company || t.name)}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-primary-900 flex items-center gap-2">
                      {t.name}
                      {t.linkedin && (
                        <a
                          href={t.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-800 transition-colors"
                        >
                          <Linkedin size={16} />
                        </a>
                      )}
                    </h3>
                    {buildHeader(t) && <span className="font-medium">{buildHeader(t)}</span>}
                    {t.date && <span className="text-sm text-primary-500 mt-1">{t.date}</span>}
                  </div>
                </div>
                <Quote size={24} className="text-primary-300" />
              </div>
              
              <div className="prose prose-primary max-w-none">
                <div className="whitespace-pre-line text-primary-700 leading-relaxed">
                  {t.full}
                </div>
              </div>

              {t.relatedProjects && t.relatedProjects.length > 0 && (
                <div className="mt-6 pt-6 border-t border-primary-100">
                  <div className="flex items-center gap-2 mb-3">
                    <Briefcase size={16} className="text-primary-600" />
                    <span className="text-sm font-medium text-primary-700">Related Projects:</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {t.relatedProjects.map((project, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                      >
                        {project}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </section>
      ))}
    </div>
  );
};

export default TestimonialsUnlocked; 